import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import type { SecurityRequirementObject } from 'openapi3-ts';
import { z } from 'zod';

extendZodWithOpenApi(z);

/** Rate limit presets */
export const rateLimitPresets = ['ai_inference', 'ai_model_training'] as const;
export type RateLimitPresetEnum = (typeof rateLimitPresets)[number];

/**
 * All custom `metadata` properties allowed on ts-rest contracts.
 * @link https://ts-rest.com/docs/core/#metadata
 * @todo Lean into this and re-work `api-route-initializer` & withTicMiddleware
 */
export type ApiContractMetadata = z.infer<typeof ApiContractMetadataSchema>;
export const ApiContractMetadataSchema = z.object({
  /** When defined, `headers` will describe rate limits */
  rateLimitPreset: z.enum(rateLimitPresets).optional(),
  /** Specify tags for endpoint */
  openApiTags: z.array(z.string()).optional(),
  /** Specify security for endpoint */
  openApiSecurity: z.custom<SecurityRequirementObject[]>().optional(),
  /**
   * Optional metadata for exposing this endpoint as an MCP tool.
   * Local development only (debugging / contextualization). Not intended for production.
   */
  mcp: z
    .object({
      expose: z
        .boolean()
        .optional()
        .describe('Expose this endpoint as an MCP tool (defaults to false).'),
      toolName: z
        .string()
        .optional()
        .describe('Optional override for the MCP tool name (must match /^[a-z0-9_-]+$/i).'),
    })
    .optional(),
});

/**
 * Convenience metadata for exposing a contract endpoint as an MCP tool.
 *
 * @remarks
 * Local development only (debugging / contextualization). Not intended for production.
 */
export const mcpExposeMetadata = {
  mcp: { expose: true },
} satisfies ApiContractMetadata;

// Duplicated from @upstash/ratelimit
type Unit = 'ms' | 's' | 'm' | 'h' | 'd';
type Duration = `${number} ${Unit}` | `${number}${Unit}`;

/**
 * Preset rate limits for different types of API requests
 * using the `@upstash/ratelimit` library.
 */
export const RATE_LIMITS: Record<
  RateLimitPresetEnum,
  { numRequests: number; windowSize: Duration; reqPerMin: number }
> = {
  ai_inference: { numRequests: 20, windowSize: '5m', reqPerMin: 20 / 5 },
  // Adjusted from 1req/5min to 30req/5min as part of AI engine deduping requests.
  ai_model_training: { numRequests: 30, windowSize: '5m', reqPerMin: 30 / 5 },
};
